import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import { alpha, useTheme } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { useMemo, useRef, useState } from 'react';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ClearIcon from '@mui/icons-material/Close';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LinearProgress from '@mui/material/LinearProgress';
import { EmptyState, WarningPanel } from '@backstage/core-components';
import { AuroraBackground, AuroraHeader } from '@mb.io/core-components';
import { ValidationsDialog } from '../../dialogs/ValidationsDialog/ValidationsDialog';
import { useScaffolderCategories } from '../../hooks/useScaffolderCategories';
import { useTemplates } from '../../hooks/useTemplates';
import { CATEGORY_ICONS } from '../icons';
import { ScaffolderCategory } from '../../types';
import { selectedTemplateRouteRef } from '../../routes';
import { useRouteRef } from '@backstage/core-plugin-api';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';

type CategoryCardProps = {
  category: ScaffolderCategory;
};

const CategoryCard = ({ category }: CategoryCardProps) => {
  const theme = useTheme();
  const icon = CATEGORY_ICONS[category.id] || <LibraryBooksIcon />;

  return (
    <Card
      component={Link}
      to={`/create/templates/${category.id}`}
      elevation={0}
      sx={{
        borderRadius: 6,
        border: `1px solid ${
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(15,20,30,0.08)'
        }`,
        backgroundImage: 'none',
        p: 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'border-color .2s ease, transform .2s ease',
        '&:hover': {
          borderColor: 'info.main',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: 'center', mb: 1.5 }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.info.main, 0.15),
            color: 'info.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg': { fontSize: 20 },
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 15,
            lineHeight: 1.3,
            flex: 1,
          }}
        >
          {category.title}
        </Typography>
      </Stack>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 1.5,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 3,
          overflow: 'hidden',
        }}
      >
        {category.summary || category.description}
      </Typography>

      <Box sx={{ flex: 1 }} />

      <Stack
        direction="row"
        sx={{
          color: 'text.secondary',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
      </Stack>
    </Card>
  );
};

export const ScaffolderLandingContentPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [validatorOpen, setValidatorOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchInputRef = useRef<HTMLDivElement>(null);
  const templateRoute = useRouteRef(selectedTemplateRouteRef);

  const { loading, error, categories } = useScaffolderCategories();
  const { entities: templates } = useTemplates();

  const handleValidatorOpen = () => setValidatorOpen(true);
  const handleValidatorClose = () => setValidatorOpen(false);

  // Filter templates based on search term
  const searchResults = useMemo(() => {
    if (!templates || searchTerm.trim().length === 0) return [];

    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return templates
      .filter(item => {
        const { name, title, tags, description } = item.metadata;
        return (
          (tags &&
            tags.some(tag =>
              tag.toLowerCase().includes(lowerCaseSearchTerm),
            )) ||
          name.toLowerCase().includes(lowerCaseSearchTerm) ||
          item.spec.type.toLowerCase().includes(lowerCaseSearchTerm) ||
          (description &&
            description.toLowerCase().includes(lowerCaseSearchTerm)) ||
          (title && title.toLowerCase().includes(lowerCaseSearchTerm))
        );
      })
      .slice(0, 8); // Limit results
  }, [templates, searchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setDropdownOpen(e.target.value.length > 0);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDropdownOpen(false);
  };

  const handleSelect = (template: TemplateEntityV1beta3) => {
    setSearchTerm('');
    setDropdownOpen(false);
    const { namespace, name } = parseEntityRef(stringifyEntityRef(template));
    navigate(templateRoute({ namespace, templateName: name }));
  };

  const handleClickAway = () => {
    setDropdownOpen(false);
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
        <WarningPanel
          severity="error"
          title="Could not load Scaffolder Categories."
        >
          {error.message}
        </WarningPanel>
      </Container>
    );
  }

  if (!categories?.length) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
        <EmptyState
          missing="info"
          title="No Scaffolder Categories to display"
          description="You haven't configured any Scaffolder categories yet."
          action={
            <Button
              variant="contained"
              color="primary"
              href="/docs/default/component/odp-user-doc/catalog/catalog-content/"
            >
              Read more
            </Button>
          }
        />
      </Container>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <AuroraBackground />
      <Container
        maxWidth="xl"
        sx={{ py: { xs: 3, md: 4 }, position: 'relative', zIndex: 1 }}
      >
        <AuroraHeader
          title="Software Templates"
          description="Create new software components through the One Developer Portal. These templates provide standardized starting points for backend services, frontend components, CI/CD pipelines, and more."
        />

        {/* Search bar and Validate button */}
        <Stack direction="row" spacing={2} sx={{ mb: 5 }} alignItems="center">
          <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{ position: 'relative', maxWidth: 460, flex: 1 }}>
              <TextField
                ref={searchInputRef}
                fullWidth
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => searchTerm.length > 0 && setDropdownOpen(true)}
                placeholder="What do you want to build?"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Clear search"
                        onClick={handleClearSearch}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
                sx={{
                  '& .MuiInputBase-root, & .v5-MuiInputBase-root': {
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.04)'
                        : 'rgba(15,20,30,0.04)',
                    borderRadius: '50px',
                    '& fieldset': {
                      borderRadius: '50px',
                    },
                  },
                }}
              />
              <Popper
                open={dropdownOpen && searchTerm.trim().length > 0}
                anchorEl={searchInputRef.current}
                placement="bottom-start"
                style={{
                  width: searchInputRef.current?.offsetWidth,
                  zIndex: 1300,
                }}
              >
                <Paper
                  elevation={8}
                  sx={{
                    mt: 1,
                    borderRadius: 2,
                    maxHeight: 400,
                    overflow: 'auto',
                    border: `1px solid ${
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.12)'
                        : 'rgba(15,20,30,0.12)'
                    }`,
                  }}
                >
                  {searchResults.length === 0 ? (
                    <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No templates found for "{searchTerm}"
                      </Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {searchResults.map((template: TemplateEntityV1beta3) => {
                        const categoryTag = template.metadata.tags?.find(
                          tag => CATEGORY_ICONS[tag],
                        );
                        const icon = categoryTag ? (
                          CATEGORY_ICONS[categoryTag]
                        ) : (
                          <LibraryBooksIcon />
                        );

                        return (
                          <ListItemButton
                            key={`${template.metadata.namespace}-${template.metadata.name}`}
                            onClick={() => handleSelect(template)}
                            sx={{
                              py: 1.5,
                              px: 2,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.info.main, 0.08),
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.info.main, 0.15),
                                color: 'info.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 2,
                                flexShrink: 0,
                                '& svg': { fontSize: 18 },
                              }}
                            >
                              {icon}
                            </Box>
                            <ListItemText
                              primary={
                                <Typography
                                  sx={{ fontWeight: 600, fontSize: 14 }}
                                >
                                  {template.metadata.title ||
                                    template.metadata.name}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 1,
                                    overflow: 'hidden',
                                  }}
                                >
                                  {template.metadata.description}
                                </Typography>
                              }
                            />
                            <ArrowForwardRoundedIcon
                              sx={{
                                fontSize: 16,
                                color: 'text.secondary',
                                ml: 1,
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  )}
                </Paper>
              </Popper>
            </Box>
          </ClickAwayListener>
          <Button
            variant="outlined"
            onClick={handleValidatorOpen}
            sx={{ px: 3, borderRadius: '50px' }}
          >
            Validate
          </Button>
        </Stack>

        {/* Categories section */}
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}
        >
          Browse by Category
        </Typography>

        <Grid container spacing={2.5}>
          {categories.map((category: ScaffolderCategory) => (
            <Grid key={category.id} item xs={12} sm={6} md={4} lg={3}>
              <CategoryCard category={category} />
            </Grid>
          ))}
        </Grid>
      </Container>

      <ValidationsDialog open={validatorOpen} onClose={handleValidatorClose} />
    </Box>
  );
};
